import { Button } from '@amore/ui/components/button'
import { DatePicker } from '@amore/ui/components/date-picker'
import { Field } from '@amore/ui/components/field'
import { FormGrid, FormLayout } from '@amore/ui/components/form-layout'
import { Input } from '@amore/ui/components/input'
import { useRef, useState } from 'react'
import { useCanEdit, useUpdate, useWedding } from '@/mock/store'
import type { WeddingIdentity } from '@/mock/types'
import { FormDialog } from '@/patterns/dialog'
import { useToast } from '@/shell/toaster'
import { datePickerLabels } from '../tasks/task-fields'
import brideIllustration from './assets/bride.svg'
import groomIllustration from './assets/groom.svg'
import {
  minimumWeddingDate,
  saveWeddingIdentity,
  validateWeddingIdentity,
  type WeddingIdentityErrors,
} from './wedding-identity-model'

export function WeddingIdentityDialog({ onClose }: { onClose: () => void }) {
  const data = useWedding()
  const canEdit = useCanEdit()
  const update = useUpdate()
  const { showToast } = useToast()
  const [draft, setDraft] = useState<WeddingIdentity>(() => ({ ...data.identity }))
  const [errors, setErrors] = useState<WeddingIdentityErrors>({})
  const formRef = useRef<HTMLFormElement>(null)
  const brideRef = useRef<HTMLInputElement>(null)
  const groomRef = useRef<HTMLInputElement>(null)
  const dateRef = useRef<HTMLButtonElement>(null)
  const dirty =
    draft.brideName !== data.identity.brideName ||
    draft.groomName !== data.identity.groomName ||
    draft.date !== data.identity.date
  const change = <K extends keyof WeddingIdentity>(field: K, value: WeddingIdentity[K]) => {
    setDraft((current) => ({ ...current, [field]: value }))
    setErrors((current) => ({ ...current, [field]: undefined }))
  }
  const save = () => {
    if (!canEdit) return
    const nextErrors = validateWeddingIdentity(draft)
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length) {
      const control = nextErrors.brideName
        ? brideRef.current
        : nextErrors.groomName
          ? groomRef.current
          : dateRef.current
      control?.focus()
      return
    }
    update((current) => saveWeddingIdentity(current, draft))
    showToast({ title: 'Údaje o svatbě uloženy', tone: 'success' })
    onClose()
  }

  return (
    <FormDialog
      open
      width="wide"
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title="Upravit údaje o svatbě"
      description="Jména a datum se zobrazují napříč plánovačem. Později je můžete kdykoliv změnit."
      dirty={dirty}
      primaryAction={{ label: 'Uložit', onClick: () => formRef.current?.requestSubmit() }}
    >
      <FormLayout
        ref={formRef}
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          save()
        }}
      >
        <FormGrid columns={1}>
          <div className="flex items-center gap-3 sm:gap-4">
            <img
              src={brideIllustration}
              alt=""
              width={112}
              height={120}
              className="w-16 shrink-0 sm:w-20"
            />
            <Field
              controlId="identity-bride-name"
              label="Jméno nevěsty"
              className="min-w-0 flex-1"
              error={errors.brideName}
            >
              <Input
                ref={brideRef}
                name="brideName"
                required
                maxLength={100}
                autoComplete="section-bride given-name"
                value={draft.brideName}
                onChange={(event) => change('brideName', event.target.value)}
              />
            </Field>
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            <Field
              controlId="identity-groom-name"
              label="Jméno ženicha"
              className="min-w-0 flex-1"
              error={errors.groomName}
            >
              <Input
                ref={groomRef}
                name="groomName"
                required
                maxLength={100}
                autoComplete="section-groom given-name"
                value={draft.groomName}
                onChange={(event) => change('groomName', event.target.value)}
              />
            </Field>
            <img
              src={groomIllustration}
              alt=""
              width={112}
              height={120}
              className="w-16 shrink-0 sm:w-20"
            />
          </div>
        </FormGrid>
        <div className="rounded-panel bg-panel-accent px-4 py-4 sm:px-5">
          <Field
            controlId="identity-wedding-date"
            label={<span className="font-heading text-lg text-action">Datum svatby</span>}
            error={errors.date}
            className="mx-auto w-full max-w-sm text-center"
          >
            <div className="flex flex-col items-center gap-2">
              <DatePicker
                ref={dateRef}
                name="date"
                value={draft.date ?? ''}
                onValueChange={(value) => change('date', value || null)}
                min={minimumWeddingDate}
                className="justify-center"
                labels={{
                  ...datePickerLabels,
                  title: 'Datum svatby',
                  placeholder: 'Vybrat datum',
                  clear: 'Bez data',
                }}
                formatDate={(date) =>
                  date.toLocaleDateString('cs-CZ', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })
                }
                formatMonth={(date) =>
                  date.toLocaleDateString('cs-CZ', { month: 'long', year: 'numeric' })
                }
              />
              <Button
                appearance="outline"
                tone="neutral"
                size="sm"
                className="aria-pressed:border-action aria-pressed:bg-card aria-pressed:text-foreground"
                aria-pressed={draft.date === null}
                onClick={() => change('date', null)}
              >
                Ještě nevíme
              </Button>
            </div>
          </Field>
        </div>
      </FormLayout>
    </FormDialog>
  )
}
